/*
 * icons.js
 *
 * Inline SVG icons replacing the original `<i class="ti ti-X">` Tabler
 * webfont references. Inline SVG works offline (PWA-friendly) and lets
 * us colour them with `currentColor` from CSS.
 *
 * SVG paths sourced from Lucide (https://lucide.dev). Add new icons by
 * extending the PATHS map below.
 *
 * Usage in template strings:
 *   `${icon('trash')}`
 *   `${icon('check', 'font-size:15px;color:#2a9d5a')}`
 */

const PATHS = {
  trash:
    '<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" x2="10" y1="11" y2="17"/><line x1="14" x2="14" y1="11" y2="17"/>',
  check:
    '<path d="M20 6 9 17l-5-5"/>',
  users:
    '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  'alert-triangle':
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  'info-circle':
    '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
  plus:
    '<path d="M5 12h14"/><path d="M12 5v14"/>',
  edit:
    '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>',
  bell:
    '<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>',
  settings:
    '<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/><circle cx="12" cy="12" r="3"/>',
  'chevron-down':
    '<path d="m6 9 6 6 6-6"/>',
  'arrow-left':
    '<path d="m12 19-7-7 7-7"/><path d="M19 12H5"/>',
  // ---- Bottom-nav icons (line-only, currentColor-friendly) ----
  // Doel/target voor "Dromen" — drie concentrische cirkels = mijlpaal/doel.
  target:
    '<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>',
  // Huis-omtrek voor "Home" (en samen-overzicht).
  'home-line':
    '<path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
  // Inkomsten: pijl naar binnen (geld komt binnen).
  'wallet-in':
    '<path d="M12 3v12"/><path d="m6 9 6 6 6-6"/><path d="M5 21h14"/>',
  // Uitgaven: pijl naar buiten (geld gaat eruit).
  'wallet-out':
    '<path d="M12 21V9"/><path d="m6 15 6-6 6 6"/><path d="M5 3h14"/>'
};

/**
 * Render an inline SVG icon at a given pixel size with optional extra
 * inline styling. Returns a raw string for embedding in template
 * literals.
 *
 * @param {keyof typeof PATHS} name
 * @param {string} [style]  Additional CSS to append to the SVG's style attr.
 * @param {number} [size]   Square size in px. Defaults to 13.
 */
export function icon(name, style = '', size = 13) {
  const body = PATHS[name];
  if (!body) {
    // Visible breakage in dev; trivial to grep for if it slips through.
    console.warn('[icons] unknown icon:', name);
    return '';
  }
  return (
    `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 24 24" ` +
    `style="${style}" aria-hidden="true">${body}</svg>`
  );
}
